import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { creditNoteApi } from '../services/creditNoteApi';
import { useInvalidateWorkOrder } from './useWorkOrders';
import type {
  ApplyCreditNoteRequest,
  StoreCreditNoteRequest,
  VoidCreditNoteRequest,
} from '../types/service.types';

/**
 * `customerLists`/`customerList` are a genuinely different collection from
 * the work-order-scoped `list` — the customer-scoped list must be
 * invalidatable independently of any one work order (D14-16's Apply dialog
 * reads it, and `useApplyCreditNote`/`useVoidCreditNote`/`useRefundPayment`
 * in `usePayments.ts` all invalidate it). `status` is part of the
 * `customerList` key so `?status=open` and `?status=all` cache separately.
 */
export const creditNoteKeys = {
  all: ['credit-notes'] as const,
  lists: () => [...creditNoteKeys.all, 'list'] as const,
  list: (workOrderId: string) => [...creditNoteKeys.lists(), workOrderId] as const,
  customerLists: () => [...creditNoteKeys.all, 'customer-list'] as const,
  customerList: (customerId: string, status: 'open' | 'all') =>
    [...creditNoteKeys.customerLists(), customerId, status] as const,
  details: () => [...creditNoteKeys.all, 'detail'] as const,
  detail: (id: string) => [...creditNoteKeys.details(), id] as const,
};

export function useCreditNotesForWorkOrder(workOrderId: string) {
  return useQuery({
    queryKey: creditNoteKeys.list(workOrderId),
    queryFn: () => creditNoteApi.listForWorkOrder(workOrderId),
    enabled: !!workOrderId,
  });
}

export function useCustomerCreditNotes(
  customerId: string,
  status: 'open' | 'all' = 'all',
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: creditNoteKeys.customerList(customerId, status),
    queryFn: () => creditNoteApi.listForCustomer(customerId, status),
    // The endpoint is Service-owned and sits behind CheckModuleEnabled, so a
    // caller that renders on tenants without the module (the CRM customer
    // page) passes `enabled: hasModule('service')` rather than eating a 403.
    enabled: !!customerId && enabled,
  });
}

/**
 * Issuing a note changes the work order's embedded `credit_notes` collection
 * and (when `apply_to_work_order` lands in the same request, D14-06)
 * `customer_open_credit_cents` too — invalidate the work order like every
 * other Service mutation, plus this note's own work-order-scoped list.
 */
export function useIssueCreditNote(workOrderId: string) {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: StoreCreditNoteRequest) => creditNoteApi.issue(workOrderId, data),
    onSuccess: () => {
      invalidateWorkOrder(workOrderId);
      queryClient.invalidateQueries({ queryKey: creditNoteKeys.list(workOrderId) });
    },
  });
}

/**
 * D14-16: applying a note changes `customer_open_credit_cents` on EVERY
 * other work order of that customer, not only this one — a fact
 * `useInvalidateWorkOrder()` cannot cover (it only knows this one work
 * order's key), so this hook adds a second, explicit invalidation of the
 * customer-scoped list rather than folding it into the shared helper.
 */
export function useApplyCreditNote(workOrderId: string) {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ApplyCreditNoteRequest }) =>
      creditNoteApi.apply(id, data),
    onSuccess: () => {
      invalidateWorkOrder(workOrderId);
      queryClient.invalidateQueries({ queryKey: creditNoteKeys.list(workOrderId) });
      queryClient.invalidateQueries({ queryKey: creditNoteKeys.customerLists() });
    },
  });
}

/**
 * D14-09: a voided note leaves the open-credit sum, so — like apply above —
 * the customer-scoped list needs its own invalidation beside the work
 * order's.
 */
export function useVoidCreditNote(workOrderId: string) {
  const invalidateWorkOrder = useInvalidateWorkOrder();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: VoidCreditNoteRequest }) =>
      creditNoteApi.void(id, data),
    onSuccess: () => {
      invalidateWorkOrder(workOrderId);
      queryClient.invalidateQueries({ queryKey: creditNoteKeys.list(workOrderId) });
      queryClient.invalidateQueries({ queryKey: creditNoteKeys.customerLists() });
    },
  });
}
